import { Dir, dx as dirDx, dy as dirDy, type Direction } from '../../sim/direction';

export interface GestureConfig {
  /** Distance (game px) a drag must cover to count as a swipe. */
  swipeThreshold: number;
  /**
   * Distance (game px) for each further direction change within the same drag. Longer than the
   * first swipe so a curving thumb does not read as a turn.
   */
  chainThreshold: number;
  /**
   * How far off an axis a stroke may be and still count, as minor/major movement (0.7 is about
   * 35 degrees). Anything more diagonal waits for clearer movement.
   */
  axisTolerance: number;
  /** A touch that moves less than this (game px) can still be a tap. */
  tapMaxMove: number;
  /** A touch held longer than this (ms) is not a tap. */
  tapMaxMs: number;
  /** A touch held still (within tapMaxMove) this long (ms) is a hold. */
  holdMs: number;
  /**
   * The hold in the swipe scheme, where a tap steers nothing and so a hold need not outlast one.
   * Quicker, since the Zero-Turn crosses a tile in about 280 ms (owner, 2026-10-10: its spin came
   * a tile too late).
   */
  swipeHoldMs: number;
}

/** Tuned for a 192px-wide game on a phone: 1 game px is roughly 2 CSS px. */
export const DEFAULT_GESTURES: GestureConfig = {
  swipeThreshold: 8,
  chainThreshold: 12,
  axisTolerance: 0.7,
  tapMaxMove: 6,
  tapMaxMs: 300,
  holdMs: 280,
  swipeHoldMs: 170,
};

export type Gesture =
  | { kind: 'swipe'; dir: Direction }
  | { kind: 'tap'; x: number; y: number }
  /**
   * A finger held still: on touching down, or at the end of a swipe (turn, then park, in one
   * stroke). It can still slide into a swipe afterwards.
   */
  | { kind: 'hold' };

interface Touch {
  /** Where the current stroke is measured from. */
  anchorX: number;
  anchorY: number;
  startX: number;
  startY: number;
  startTime: number;
  /** The last direction this touch fired, if any. */
  lastDir: Direction | null;
  /** Fired a hold since touching down or since the last swipe (and so is not a tap). */
  held: boolean;
  /** Fired a hold at all (a touch that held is never a tap). */
  everHeld: boolean;
  /** Where the finger is now. */
  x: number;
  y: number;
  /** Where the finger came to rest, and when: a hold is measured from here. */
  restX: number;
  restY: number;
  restTime: number;
}

/**
 * Turns raw pointer streams into swipes and taps. Swipes fire mid-drag (no waiting for release),
 * and one continuous drag can chain direction changes (e.g. right-then-down for a U-turn).
 *
 * Robustness rules (lm-d4m.2):
 * - A stroke must be clearly along one axis; diagonals wait for more movement.
 * - After a swipe, the anchor follows the finger for as long as it keeps going mostly the same
 *   way, so a thumb that curves at the end of a stroke never builds up a sideways turn. Only a
 *   real corner (movement mostly across or against the last direction) can fire again, and it
 *   needs the longer {@link GestureConfig.chainThreshold}.
 * - Keeping on in the same direction never fires twice.
 */
export class GestureRecognizer {
  private readonly touches = new Map<number, Touch>();

  constructor(private readonly config: GestureConfig = DEFAULT_GESTURES) {}

  down(id: number, x: number, y: number, timeMs: number): void {
    this.touches.set(id, {
      anchorX: x,
      anchorY: y,
      startX: x,
      startY: y,
      startTime: timeMs,
      lastDir: null,
      held: false,
      everHeld: false,
      x,
      y,
      restX: x,
      restY: y,
      restTime: timeMs,
    });
  }

  /**
   * Called every frame: a touch held still for `holdMs` (default {@link GestureConfig.holdMs}) is
   * a hold, once on touching down and once after each swipe.
   */
  poll(id: number, timeMs: number, holdMs = this.config.holdMs): Gesture | null {
    const t = this.touches.get(id);
    if (!t || t.held || timeMs - t.restTime < holdMs) return null;
    t.held = true;
    t.everHeld = true;
    return { kind: 'hold' };
  }

  move(id: number, x: number, y: number, timeMs: number): Gesture | null {
    const t = this.touches.get(id);
    if (!t) return null;
    t.x = x;
    t.y = y;
    // Still resting where it stopped? Otherwise the rest (and the hold's clock) starts again here.
    if (Math.max(Math.abs(x - t.restX), Math.abs(y - t.restY)) > this.config.tapMaxMove) {
      t.restX = x;
      t.restY = y;
      t.restTime = timeMs;
      // Before any swipe, a touch that wandered is a drag, never a hold.
      if (t.lastDir === null) t.held = true;
    }
    const dx = x - t.anchorX;
    const dy = y - t.anchorY;
    if (t.lastDir !== null) {
      // Still heading the way we last fired (forward at least as much as sideways): follow.
      const forward = dx * dirDx(t.lastDir) + dy * dirDy(t.lastDir);
      const sideways = Math.abs(dx * dirDy(t.lastDir) - dy * dirDx(t.lastDir));
      if (forward >= sideways) {
        t.anchorX = x;
        t.anchorY = y;
        return null;
      }
    }
    const major = Math.max(Math.abs(dx), Math.abs(dy));
    const minor = Math.min(Math.abs(dx), Math.abs(dy));
    const needed = t.lastDir === null ? this.config.swipeThreshold : this.config.chainThreshold;
    if (major < needed || minor > major * this.config.axisTolerance) return null;
    return this.fire(t, x, y, dominant(dx, dy));
  }

  up(id: number, x: number, y: number, timeMs: number): Gesture | null {
    const swipe = this.move(id, x, y, timeMs);
    const t = this.touches.get(id);
    this.touches.delete(id);
    if (swipe || !t || t.lastDir !== null || t.everHeld) return swipe;
    const dx = x - t.startX;
    const dy = y - t.startY;
    const moved = Math.max(Math.abs(dx), Math.abs(dy));
    // A clear flick that stayed diagonal the whole way: take its dominant axis.
    if (moved >= this.config.swipeThreshold) return { kind: 'swipe', dir: dominant(dx, dy) };
    if (moved > this.config.tapMaxMove || timeMs - t.startTime > this.config.tapMaxMs) return null;
    return { kind: 'tap', x: t.startX, y: t.startY };
  }

  cancel(id: number): void {
    this.touches.delete(id);
  }

  private fire(t: Touch, x: number, y: number, dir: Direction): Gesture {
    t.lastDir = dir;
    t.anchorX = x;
    t.anchorY = y;
    // The finger may now come to rest for a hold (turn, then park).
    t.held = false;
    return { kind: 'swipe', dir };
  }
}

/** The axis a movement mostly follows (y grows downward); exact diagonals go horizontal. */
function dominant(dx: number, dy: number): Direction {
  if (Math.abs(dx) >= Math.abs(dy)) return dx > 0 ? Dir.E : Dir.W;
  return dy > 0 ? Dir.S : Dir.N;
}
